/**
 * SANAL HESAP NOTLARI — Varlık Dökümü'nde sanal hesap seçiliyken (back-test,
 * 14.10.2026).
 *
 * Üreticinin söyledikleri: üretilemediyse sebebi (uyarı), sonucu etkileyen
 * eksikler (uyarı: temettü bilgisi yok, çekim karşılanamadı…), açıklamalar
 * (bilgi) ve bekleyen nakit. Altında kuraldan türetilen hareketlerin salt
 * okunur dökümü — elle düzenlenemez (kullanıcı kararı 14.10.2026).
 *
 * Sabit not (K7) uyarı DEĞİL, düz metin: "her işaret bir anormallik" kuralı.
 */

import { useCallback, useState } from "react";
import type { SanalHareketDokumu, SanalHesapTanimi } from "@parafon/contracts/local-api";
import { formatDecimal, formatMoney, MIKTAR_HANE } from "../lib/decimal";
import { TRANSACTION_TYPE_LABEL, assetLabel, formatIsoDate } from "../lib/format";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync } from "../repo/useAsync";
import { Notice, cx } from "../ui/primitives";
import { M } from "../dil";
import { motorMetni } from "../dil/motorMetni";

export const SABIT_NOT = M.sanal.sabitNot;

export function SanalNotlar({ tanim, asOf }: { readonly tanim: SanalHesapTanimi; readonly asOf?: string | undefined }) {
  const { repo, revision } = useRepositoryContext();
  const [dokumAcik, setDokumAcik] = useState(false);
  const yukle = useCallback(async (): Promise<SanalHareketDokumu | null> => {
    if (repo.sanalHesaplar === undefined) return null;
    return repo.sanalHesaplar.hareketler(tanim.id, asOf);
  }, [repo, tanim, asOf]);
  const { state } = useAsync(yukle, [repo, revision, tanim, asOf]);
  const d = state.data ?? null;
  const uyarilar = d?.notlar.filter((n) => n.ton === "uyari") ?? [];
  const bilgiler = d?.notlar.filter((n) => n.ton === "bilgi") ?? [];
  const islemler = d?.hareketler ?? [];

  return (
    <section aria-label={M.sanal.notlar.bolum} className="space-y-2 px-4 pt-3">
      <p className="text-[12px] text-muted">{SABIT_NOT}</p>
      {d?.hata != null && (
        <Notice tone="warn" title={M.sanal.notlar.uretilemedi}>
          {motorMetni(d.hata)}
        </Notice>
      )}
      {uyarilar.length > 0 && (
        <Notice tone="warn" title={M.sanal.notlar.eksikler}>
          <ul className="list-disc pl-4">
            {uyarilar.map((n, i) => (
              <li key={i}>
                {n.tarih !== null && <span className="tabular-nums">{formatIsoDate(n.tarih)}: </span>}
                {motorMetni(n.metin)}
              </li>
            ))}
          </ul>
        </Notice>
      )}
      {(bilgiler.length > 0 || (d !== null && d.bekleyenNakit !== "0")) && (
        <ul className="space-y-0.5 text-[12px] text-muted">
          {bilgiler.map((n, i) => (
            <li key={i}>{motorMetni(n.metin)}</li>
          ))}
          {d !== null && d.bekleyenNakit !== "0" && (
            <li>
              {M.sanal.notlar.bekleyenNakit}{" "}
              <span className="pf-tek">
                {formatMoney(d.bekleyenNakit)} {tanim.paraBirimi}
              </span>
            </li>
          )}
        </ul>
      )}
      {islemler.length > 0 && (
        <div>
          <button
            type="button"
            aria-expanded={dokumAcik}
            onClick={() => setDokumAcik((a) => !a)}
            className="text-[12px] text-accent"
          >
            {dokumAcik ? M.sanal.notlar.gizle : M.sanal.notlar.goster(islemler.length)}
          </button>
          {dokumAcik && (
            <div className="mt-2 max-h-80 overflow-auto rounded-[var(--pf-radius-sm)] border border-line">
              <table className="w-full text-[12px]">
                <caption className="sr-only">{M.sanal.notlar.tabloBaslik}</caption>
                <thead className="sticky top-0 bg-surface text-left text-muted">
                  <tr>
                    <th className="px-2 py-1">{M.sanal.notlar.tarih}</th>
                    <th className="px-2 py-1">{M.sanal.notlar.tur}</th>
                    <th className="px-2 py-1">{M.sanal.notlar.varlik}</th>
                    <th className="px-2 py-1 text-right">{M.sanal.notlar.miktar}</th>
                    <th className="px-2 py-1 text-right">{M.sanal.notlar.fiyatTutar}</th>
                    <th className="px-2 py-1 text-right">{M.sanal.notlar.komisyon}</th>
                  </tr>
                </thead>
                <tbody>
                  {islemler.map((t) => {
                    const nakit = t.type === "DEPOSIT" || t.type === "WITHDRAWAL" || t.type === "DIVIDEND";
                    return (
                      <tr key={t.id} className={cx("border-t border-line")}>
                        <td className="px-2 py-1 tabular-nums">{formatIsoDate(t.tradeDate)}</td>
                        <td className="px-2 py-1">{TRANSACTION_TYPE_LABEL[t.type]}</td>
                        <td className="px-2 py-1">{t.asset.kind === "manual" ? M.sanal.notlar.nakit : assetLabel(t.asset)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {nakit ? "" : formatDecimal(t.quantity, { maxFractionDigits: MIKTAR_HANE })}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {formatDecimal(t.price, { maxFractionDigits: 6 })} {t.currency}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{t.fee === undefined ? "" : formatMoney(t.fee)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
